import { Component, Input, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { LocalUserRoles } from '@buspro/shared/common';
import { LocalUserDto, OperationsApi } from '@buspro/web/apis';
import { AppService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { AvatarModule } from 'ngx-avatars';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AdminMenuItemComponent } from './admin-menu-item.component';
import { CompanyStatusComponent } from './company-status.component';

export type MenuItem = {
  icon: string;
  label: string;
  route?: string;
  subItems?: MenuItem[];
  isTenantSpecific: boolean;
  badge?: string | number; // Add badge support
};

@Component({
  selector: 'buspro-admin-sidenav',
  standalone: true,
  imports: [MatListModule, MatIconModule, AvatarModule, AdminMenuItemComponent, CompanyStatusComponent],
  template: `
    @if (currentTenant(); as tenant) {
    <div class="pt-8 text-center flex flex-col items-center">
      <ngx-avatars
        [size]="profilePicSize()"
        [name]="tenant.name"
        [src]="tenant.logoUrl"
      ></ngx-avatars>
      <div
        [style.height]="'3rem'"
        [class.!h-0]="sideNavCollapsed()"
        [class.opacity-0]="sideNavCollapsed()"
      >
        <div class="font-bold ">{{ tenant.name }}</div>
        <buspro-admin-company-status
          [status]="tenant.status"
        ></buspro-admin-company-status>
      </div>
    </div>
    }

    <mat-nav-list>
      @for (menuItem of menuItems(); track menuItem) {
      <buspro-admin-menu-item
        [item]="menuItem"
        [collapsed]="sideNavCollapsed()"
      ></buspro-admin-menu-item>
      }
    </mat-nav-list>
  `,
  styles: `

    .menu-item {
      border-left: 5px solid;
      border-left-color: rgba(0, 0, 0, 0);
    }

    .selected-menu-item {
      border-left-color: var(--primary-color);
      background: rgba(0, 0, 0, 0.05)
    }

    :host * {
      transition: all 300ms ease-in-out;
    }
  `,
})
export class AdminSidenavComponent {
  appService = inject(AppService);
  currentTenant = this.appService.currentTenant$;
  private operationsApi = inject(OperationsApi);
  private ngxAuthService = inject(NgxAuthService);
  private logger = inject(NGXLogger);

  authUser: Signal<LocalUserDto> = toSignal(this.ngxAuthService.state);

  // Query for audits in progress to show badge.
  // Oversight roles may hold several, so the badge counts them rather than reporting 1.
  currentAuditQuery = injectQuery(() => ({
    queryKey: ['sidebar-current-audit-badge'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetMyAuditsInProgress()),
    refetchInterval: 30000, // Refresh every 30 seconds
  }));

  // Query for operations ready for audit
  readyForAuditQuery = injectQuery(() => ({
    queryKey: ['sidebar-ready-for-audit-badge'],
    queryFn: () => lastValueFrom(this.operationsApi.operationsControllerGetOperationsReadyForAudit()),
    refetchInterval: 30000, // Refresh every 30 seconds
  }));

  // Computed signal for audit in progress count
  auditInProgressCount = computed(() => this.currentAuditQuery.data()?.length ?? 0);

  // Computed signal for pending audits count
  pendingAuditsCount = computed(() => this.readyForAuditQuery.data()?.length ?? 0);

  // Dynamic badge for "Ready for Audit" menu item - show fire emoji when there are pending audits
  readyForAuditBadge = computed(() => (this.pendingAuditsCount() > 0 ? '🔥' : undefined));

  // Dynamic badge for parent "Audits" menu
  // Badge will be shown/hidden by menu item component based on:
  // - Sidebar collapsed: Badge on icon
  // - Sidebar expanded + menu collapsed: Badge on label
  // - Sidebar expanded + menu expanded: No badge (sub-items show their badges)
  auditParentBadge = computed(() => {
    if (this.auditInProgressCount() > 0) {
      return this.auditInProgressCount(); // Show "1" when user has audit in progress
    }
    if (this.pendingAuditsCount() > 0) {
      return '🔥'; // Show fire emoji when operations are waiting
    }
    return undefined; // Show nothing
  });

  // Check if user is a BUSPRO Bus Stop Manager or Clerk
  private isBusproBusStopStaff = computed(() => {
    const role = this.authUser()?.role;
    return role === LocalUserRoles.BUSPRO_BUS_STOP_MANAGER || role === LocalUserRoles.BUSPRO_BUS_STOP_CLERK;
  });

  // Check if user is an Auditor
  private isAuditor = computed(() => {
    const role = this.authUser()?.role;
    return role === LocalUserRoles.LOCAL_AUDITOR;
  });

  // Check if user is a Technician
  private isTechnician = computed(() => {
    const role = this.authUser()?.role;
    return role === LocalUserRoles.LOCAL_TECHNICIAN;
  });

  // Check if user is a Local Admin or Manager (should see Overview)
  // Note: Bus Stop staff are excluded from this - they have their own dashboard
  private isLocalAdminOrManager = computed(() => {
    const role = this.authUser()?.role;
    return (
      role === LocalUserRoles.LOCAL_ADMIN ||
      role === LocalUserRoles.LOCAL_MANAGER ||
      role === LocalUserRoles.LOCAL_DEVELOPER ||
      role === LocalUserRoles.LOCAL_AUDITOR ||
      role === LocalUserRoles.LOCAL_TECHNICIAN
    );
  });

  constructor() {
    effect(() => {
      const user = this.authUser();
      this.logger.info('[AdminSidenav] current user', user);
      this.logger.info('[AdminSidenav] isAuditor flag', this.isAuditor());
      this.logger.info('[AdminSidenav] isBusproBusStopStaff flag', this.isBusproBusStopStaff());
      this.logger.info('[AdminSidenav] isLocalAdminOrManager flag', this.isLocalAdminOrManager());
    });
  }

  // Make menuItems a computed signal so it reacts to auditInProgressCount changes
  menuItems = computed<MenuItem[]>(() => {
    const busStopsSubItems: MenuItem[] = [];

    // Overview - for Local Admins, Managers (bus stop staff see this in their dashboard)
    if (this.isLocalAdminOrManager()) {
      busStopsSubItems.push({
        icon: 'view_list',
        label: 'Overview',
        route: 'overview',
        isTenantSpecific: false,
      });
    }

    // Managed Bus Stops - ONLY for top-level roles (Local Admins, Managers, Developers)
    // BusPro staff should NOT have access - they use Live Bookings page instead
    if (this.isLocalAdminOrManager()) {
      busStopsSubItems.push({
        icon: 'manage_accounts',
        label: 'Managed Bus Stops',
        route: 'managed-bus-stops',
        isTenantSpecific: false,
      });
    }

    // Live Bookings - for Local Admins, Managers, and BUSPRO staff
    if (this.isLocalAdminOrManager() || this.isBusproBusStopStaff()) {
      busStopsSubItems.push({
        icon: 'event_available',
        label: 'Live Bookings',
        route: 'live-bookings',
        isTenantSpecific: false,
      });
    }

    // Management Requests - for Local Admins, Managers, and BUSPRO staff
    if (this.isLocalAdminOrManager()) {
      busStopsSubItems.push({
        icon: 'pending_actions',
        label: 'Management Requests',
        route: 'management-requests',
        isTenantSpecific: false,
      });
    }

    // Staff Assignments - for Local Admins, Managers, and BUSPRO staff
    if (this.isLocalAdminOrManager()) {
      busStopsSubItems.push({
        icon: 'assignment_ind',
        label: 'Staff Assignments',
        route: 'staff-assignments',
        isTenantSpecific: false,
      });
    }

    // Build dashboard menu items based on role
    const dashboardMenuItems: MenuItem[] = [];

    // Main Dashboard - only for LOCAL_ADMIN, LOCAL_MANAGER, and LOCAL_DEVELOPER
    const role = this.authUser()?.role;
    const isMainDashboardRole =
      role === LocalUserRoles.LOCAL_ADMIN ||
      role === LocalUserRoles.LOCAL_MANAGER ||
      role === LocalUserRoles.LOCAL_DEVELOPER;

    if (isMainDashboardRole) {
      dashboardMenuItems.push({
        icon: 'widgets',
        label: 'Main Dashboard',
        route: '/dashboard',
        isTenantSpecific: false,
      });
    }

    // Bus Stop Dashboard - only for bus stop managers/clerks
    if (this.isBusproBusStopStaff()) {
      dashboardMenuItems.push({
        icon: 'dashboard',
        label: 'Bus Stop Dashboard',
        route: '/bus-stop-dashboard',
        isTenantSpecific: false,
      });
    }

    // Auditors Dashboard - only for auditors
    if (this.isAuditor()) {
      dashboardMenuItems.push({
        icon: 'audit',
        label: 'Auditors Dashboard',
        route: '/auditors-dashboard',
        isTenantSpecific: false,
      });
    }

    // Technicians Dashboard - only for technicians
    if (this.isTechnician()) {
      dashboardMenuItems.push({
        icon: 'build',
        label: 'Technicians Dashboard',
        route: '/technicians-dashboard',
        isTenantSpecific: false,
      });
    }

    // Sync Watch - platform staff who are pushed its alerts (the backend's
    // /sync-health/watch is restricted to the same four roles)
    const isSyncWatchRole =
      role === LocalUserRoles.LOCAL_ADMIN ||
      role === LocalUserRoles.LOCAL_MANAGER ||
      role === LocalUserRoles.LOCAL_DEVELOPER ||
      role === LocalUserRoles.LOCAL_TECHNICIAN;
    if (isSyncWatchRole) {
      dashboardMenuItems.push({
        icon: 'sync_problem',
        label: 'Sync Watch',
        route: '/sync-watch',
        isTenantSpecific: false,
      });
    }

    return [
      ...dashboardMenuItems,
      {
        icon: 'domain',
        label: 'Tenants',
        route: `/tenants`,
        isTenantSpecific: true,
      },
      {
        icon: 'explore_nearby',
        label: 'Official Places',
        route: '/standard-places',
        isTenantSpecific: false,
      },
      {
        icon: 'directions_bus',
        label: 'Bus Types',
        route: '/bus-types',
        isTenantSpecific: false,
      },
      {
        icon: 'traffic',
        label: 'Bus Stops',
        route: '/bus-stops', // Parent route - clicking will navigate to overview
        isTenantSpecific: false,
        subItems: busStopsSubItems.length > 0 ? busStopsSubItems : undefined,
      },
      {
        icon: 'inventory_2',
        label: 'Inventory',
        route: '/inventory',
        isTenantSpecific: false,
        subItems: [
          {
            icon: 'inventory',
            label: 'Stocks Available',
            route: 'stocks-available',
            isTenantSpecific: false,
          },
          {
            icon: 'warehouse',
            label: 'Inventory Items',
            route: 'items',
            isTenantSpecific: false,
          },
          {
            icon: 'history',
            label: 'Provisioning History',
            route: 'provisioning',
            isTenantSpecific: false,
          },
          {
            icon: 'assignment',
            label: 'All Provisioned Resources',
            route: 'provisioned-resources',
            isTenantSpecific: false,
          },
        ],
      },
      {
        icon: 'fact_check',
        label: 'Audits',
        route: '/audits',
        isTenantSpecific: false,
        badge: this.auditParentBadge(), // Dynamic: "1" if in progress, "🔥" if pending, nothing otherwise
        subItems: (() => {
          const auditSubItems: MenuItem[] = [
            {
              icon: 'list_alt',
              label: 'Ready for Audit',
              route: 'ready',
              isTenantSpecific: false,
              badge: this.readyForAuditBadge(), // Fire emoji when there are pending audits
            },
            {
              icon: 'person',
              label: 'My Audits',
              route: 'my-audits',
              isTenantSpecific: false,
              badge: this.auditInProgressCount(), // "1" when user has audit in progress
            },
          ];

          if (!this.isAuditor()) {
            auditSubItems.push({
              icon: 'description',
              label: 'Audit Reports',
              route: 'reports',
              isTenantSpecific: false,
            });
          }

          return auditSubItems;
        })(),
      },
      {
        icon: 'supervisor_account',
        label: 'Users',
        route: '/locals/users',
        isTenantSpecific: false,
      },
      {
        icon: 'phone_android',
        label: 'App Distribution',
        route: '/app-distribution',
        isTenantSpecific: false,
      },
    ];
  });

  sideNavCollapsed = signal(false);
  profilePicSize = computed(() => (this.sideNavCollapsed() ? 32 : 100));

  @Input() set collapsed(val: boolean) {
    this.sideNavCollapsed.set(val);
  }
}
